import { WS_URL } from '../config';

// Client -> Server messages
export type ClientToServerMessage =
  | { type: 'join'; role: 'streamer' | 'viewer'; label?: string }
  | { type: 'offer'; sdp: RTCSessionDescriptionInit; targetStreamId?: string }
  | { type: 'answer'; sdp: RTCSessionDescriptionInit; streamId: string }
  | { type: 'ice-candidate'; candidate: RTCIceCandidateInit; streamId: string }
  | { type: 'request-offer'; streamId: string };

// Server -> Client messages
export type ServerToClientMessage =
  | { type: 'joined'; streamId?: string }
  | { type: 'stream-added'; streamId: string; label: string }
  | { type: 'stream-removed'; streamId: string }
  | { type: 'request-offer'; streamId: string }
  | { type: 'offer'; sdp: RTCSessionDescriptionInit; streamId: string }
  | { type: 'answer'; sdp: RTCSessionDescriptionInit; streamId: string }
  | { type: 'ice-candidate'; candidate: RTCIceCandidateInit; streamId: string };

export type SignalingMessage = ServerToClientMessage;

export interface SignalingCallbacks {
  onMessage: (message: ServerToClientMessage) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: Error) => void;
}

export class SignalingClient {
  private ws: WebSocket | null = null;
  private eventId: string;
  private callbacks: SignalingCallbacks;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private isConnecting = false;
  private shouldReconnect = true;

  constructor(eventId: string, callbacks: SignalingCallbacks) {
    this.eventId = eventId;
    this.callbacks = callbacks;
  }

  connect(): void {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      console.log('[SignalingClient] Already connecting or connected');
      return;
    }

    this.shouldReconnect = true;
    this.isConnecting = true;

    const wsUrl = `${WS_URL}/ws/${this.eventId}`;
    console.log('[SignalingClient] Connecting to:', wsUrl);

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('[SignalingClient] WebSocket connected');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.callbacks.onConnected?.();
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as ServerToClientMessage;
        console.log('[SignalingClient] Received message:', message);
        this.callbacks.onMessage(message);
      } catch (e) {
        console.error('[SignalingClient] Failed to parse signaling message:', e);
      }
    };

    this.ws.onclose = () => {
      console.log('[SignalingClient] WebSocket closed');
      this.isConnecting = false;
      this.callbacks.onDisconnected?.();

      if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error('[SignalingClient] WebSocket error:', error);
      this.isConnecting = false;
      this.callbacks.onError?.(new Error('WebSocket error'));
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 16000);
    this.reconnectAttempts++;

    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  send(message: ClientToServerMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('[SignalingClient] Sending message:', message);
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('[SignalingClient] Cannot send message, WebSocket not open:', message);
    }
  }

  disconnect(): void {
    this.shouldReconnect = false;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
